import { motion } from "framer-motion";

/* =========================================================================
 * Phase 39 (Feb 18 2026) — Pulse page out-of-the-box redesign.
 *
 * Comic-panel grid with hand-drawn metaphor illustrations. The backend
 * engine (CAPE / LI / PW / CIN / shear / METAR) keeps computing the
 * authoritative numbers — the UI just paints metaphors on top so the
 * sky speaks human.
 *
 *   ☔  Will it rain        → umbrella with falling drops (intensity = drop count)
 *   💧  Air moisture        → sponge being squeezed (drips = saturation)
 *   ⛽  Storm fuel          → fuel-gauge needle (CAPE)
 *   🌀  Wind stirring       → stacked wind ribbons (shear)
 *   🌡️  Outside feel        → thermometer with mood-face in the bulb
 *   ✈️  Airport report      → plane on runway with weather glyph above
 *
 * 2-column × 3-row grid below a mood hero band that mirrors the engine's
 * primary verdict. No raw numbers shown — everything is bands + metaphor.
 * ===================================================================== */

/* ---------------- helpers (calc kept identical to the old file) ---------------- */

function computeStormPotential(c) {
  const ca = c?.convective_alert;
  const hr = c?.heavy_rain;
  const radar = c?.radar_pixel;
  const score = c?.score ?? 0;
  const label = c?.label;
  const driver = c?.score_driver;
  const headline = c?.headline;

  if (driver === "observed" || radar?.raining) {
    const intensityWord = String(
      radar?.intensity ||
        c?.metar?.observation?.intensity ||
        (headline || "").toLowerCase().match(/(heavy|moderate|light)/)?.[1] ||
        "light"
    ).toLowerCase();
    let pct = 55;
    let level = "light";
    if (intensityWord.includes("heavy")) {
      pct = 92;
      level = "heavy";
    } else if (intensityWord.includes("moderate")) {
      pct = 75;
      level = "moderate";
    }
    return {
      mood: "rain",
      level,
      pct,
      title: headline || `${intensityWord.charAt(0).toUpperCase()}${intensityWord.slice(1)} rain right now`,
      sub: "It's actively raining at your spot.",
      tone: intensityWord.includes("heavy") ? "rose" : intensityWord.includes("moderate") ? "amber" : "sky",
    };
  }
  if (ca?.level === "alert" || label === "watch_severe") {
    return { mood: "storm", level: "high", pct: 88, title: headline || "Storms charging the air", sub: "Lightning + thunder can pop up before any rain falls.", tone: "violet" };
  }
  if (label === "high" || score >= 75) {
    return { mood: "rain", level: "high", pct: Math.max(75, Math.min(100, score)), title: headline || "Rain very likely soon", sub: c?.explanation || "Models agree — keep an umbrella close.", tone: "rose" };
  }
  if (label === "moderate" || score >= 55) {
    return { mood: "rain", level: "moderate", pct: Math.max(55, Math.min(100, score)), title: headline || "Rain is possible soon", sub: c?.explanation || "There's a fair chance in the next few hours.", tone: "amber" };
  }
  if (ca?.level === "watch" || label === "watch") {
    return { mood: "storm", level: "moderate", pct: 55, title: headline || "Pop-up storms are possible", sub: "Atmosphere has fuel but it might never fire.", tone: "amber" };
  }
  if (hr?.level === "high") {
    return { mood: "rain", level: "moderate", pct: 65, title: "Heavy rain set-up", sub: hr.headline || "Conditions look ripe for heavier rain.", tone: "amber" };
  }
  return { mood: "calm", level: "calm", pct: Math.max(8, Math.min(30, score || 12)), title: "Calm hours ahead", sub: "No storm signals — the sky stays friendly.", tone: "emerald" };
}

function airMoisture(s) {
  const mm = s?.precipitable_water_mm;
  const label = s?.precipitable_water_label;
  if (mm == null) return { word: "Unknown", desc: "No moisture read.", level: 0 };
  if (label === "monsoon-grade") return { word: "Drenched", desc: "Air is holding monsoon-grade moisture.", level: 5 };
  if (label === "tropical") return { word: "Very humid", desc: "Tropical air mass — heavy rain capacity.", level: 4 };
  if (label === "moist") return { word: "Comfortable", desc: "A normal amount of moisture aloft.", level: 3 };
  if (label === "dry") return { word: "Dry", desc: "Air is on the drier side.", level: 2 };
  return { word: "Bone dry", desc: "Very little moisture in the air column.", level: 1 };
}

function stormFuel(c) {
  let cape = c?.convective_alert?.cape_jkg ?? c?.sounding?.cape_now ?? null;
  if (cape == null) {
    const sig = (c?.signals || []).find((s) => s?.id === "cape");
    if (sig && typeof sig.value === "string") {
      const m = sig.value.match(/(-?\d+(?:\.\d+)?)/);
      if (m) cape = Number(m[1]);
    }
  }
  if (cape == null || Number.isNaN(cape)) {
    // CAPE wasn't returned by the engine — but if we have K-Index / Total Totals
    // / CIN data, we can still infer the storm-fuel state: a stable atmosphere
    // (low K, low TT, or strong cap) just means "no fuel" → "Calm", not
    // "Unknown". Phase 41 Finding A.
    const s = c?.sounding || {};
    const k = s.k_index;
    const tt = s.total_totals;
    const cin = s.cin_jkg;
    const haveStableSignals = k != null || tt != null || cin != null;
    if (haveStableSignals) {
      const stable =
        (k != null && k < 25) ||
        (tt != null && tt < 44) ||
        (cin != null && Math.abs(cin) >= 80);
      if (stable) {
        return { word: "Calm", desc: "Atmosphere is at rest — no thunderstorm fuel.", level: 1 };
      }
      // K/TT marginal but no CAPE — treat as light fuel.
      return { word: "Light", desc: "Limited fuel — storms unlikely.", level: 2 };
    }
    return { word: "Unknown", desc: "No fuel reading.", level: 0 };
  }
  if (cape >= 2500) return { word: "Loaded", desc: "Severe-storm fuel — explosive when triggered.", level: 5 };
  if (cape >= 1500) return { word: "Charged", desc: "Plenty of fuel for thunderstorms.", level: 4 };
  if (cape >= 800) return { word: "Active", desc: "Pop-up storms are possible.", level: 3 };
  if (cape >= 300) return { word: "Light", desc: "Limited fuel — storms unlikely.", level: 2 };
  return { word: "Calm", desc: "Atmosphere is at rest.", level: 1 };
}

function windStirring(s) {
  const kmh = s?.bulk_shear_0_6km_kmh;
  if (kmh == null) return { word: "Unknown", desc: "No wind-layer reading.", level: 0 };
  if (kmh >= 75) return { word: "Strong", desc: "Layered winds — storms could last longer.", level: 5 };
  if (kmh >= 45) return { word: "Lively", desc: "Some shear — multicell potential.", level: 4 };
  if (kmh >= 25) return { word: "Mild", desc: "A bit of shear — short storms only.", level: 3 };
  return { word: "Calm", desc: "Wind is uniform — any storms would be brief.", level: 2 };
}

function comfortBand(h, c) {
  if (!h) return null;
  const hi = h.heat_index_c;
  const band = (h.safety_band || "comfortable").toLowerCase();
  const isRainingNow = c?.score_driver === "observed" || c?.radar_pixel?.raining;
  const rainExpected = (c?.score ?? 0) >= 55 || ["high", "moderate"].includes(c?.label);

  if (band.startsWith("extreme")) return { word: "Danger", desc: "Stay indoors — wet-bulb near survival limits.", face: "danger", feels: hi };
  if (band.startsWith("danger")) return { word: "Hot", desc: "Avoid strenuous activity. Hydrate often.", face: "hot", feels: hi };
  if (band.startsWith("caution")) return { word: "Warm", desc: isRainingNow ? "Hot and wet — feels even hotter." : "Limit midday sun. Drink water.", face: "warm", feels: hi };
  if (band === "warm") return { word: isRainingNow ? "Sticky" : "Warm", desc: isRainingNow ? "Sticky outdoors — rain plus warm air." : "Comfortable. Hat + water on long walks.", face: "warm", feels: hi };
  if (isRainingNow) return { word: "Wet", desc: "Comfy temperature, but it's raining.", face: "neutral", feels: hi };
  if (rainExpected) return { word: "Soon", desc: "Comfy now, rain is on the way.", face: "neutral", feels: hi };
  return { word: "Lovely", desc: "Pleasant feel-temperature — enjoy the outdoors.", face: "happy", feels: hi };
}

function metarStatus(m) {
  if (!m?.observation || !m?.station) return null;
  const o = m.observation;
  const station = m.station.name || m.station.icao;
  const distance = `${Math.round(m.station.distance_km)} km away`;
  if (o.raining) return { word: "Rain", desc: `${station} (${distance}) reports ${o.intensity || "active"} rain${o.thunder ? " with thunder" : ""}.`, mood: "rain" };
  if (o.thunder) return { word: "Thunder", desc: `${station} (${distance}) hears thunder.`, mood: "storm" };
  return { word: "Clear", desc: `${station} (${distance}) reports calm conditions.`, mood: "calm" };
}

/* ---------------- mood palette ---------------- */

const TONE = {
  rose: { ink: "#be123c", fill: "#fda4af", soft: "rgba(244,63,94,0.10)", border: "rgba(244,63,94,0.30)" },
  violet: { ink: "#6d28d9", fill: "#c4b5fd", soft: "rgba(139,92,246,0.10)", border: "rgba(139,92,246,0.30)" },
  amber: { ink: "#b45309", fill: "#fcd34d", soft: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.32)" },
  emerald: { ink: "#047857", fill: "#86efac", soft: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.30)" },
  sky: { ink: "#0369a1", fill: "#7dd3fc", soft: "rgba(14,165,233,0.10)", border: "rgba(14,165,233,0.30)" },
  slate: { ink: "#475569", fill: "#cbd5e1", soft: "rgba(100,116,139,0.10)", border: "rgba(100,116,139,0.28)" },
};

function tonByLevel(level) {
  if (level >= 5) return "rose";
  if (level >= 4) return "amber";
  if (level >= 3) return "sky";
  if (level >= 2) return "emerald";
  return "slate";
}

/* ============================================================
 * HAND-DRAWN METAPHOR ILLUSTRATIONS (pure SVG)
 * Each takes a `level` 0-5 and renders a different visual state.
 * Stroke-based line-art, friendly, animated with Framer Motion.
 * ============================================================ */

/** ☔ Umbrella with falling rain — "Will it rain" */
function UmbrellaRain({ level, color }) {
  const open = level >= 2;
  const drops = Math.max(0, Math.min(7, level >= 5 ? 7 : level >= 4 ? 5 : level >= 3 ? 3 : level >= 2 ? 2 : 0));
  return (
    <svg viewBox="0 0 100 90" className="w-full h-full" aria-hidden>
      {/* Falling drops */}
      {Array.from({ length: drops }).map((_, i) => (
        <motion.line
          key={i}
          x1={20 + i * 9}
          y1={4}
          x2={20 + i * 9}
          y2={14}
          stroke={color}
          strokeWidth="1.6"
          strokeLinecap="round"
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: [0, 1, 0], y: [0, 22, 26] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.16, ease: "easeIn" }}
        />
      ))}
      {/* Umbrella canopy */}
      {open ? (
        <g>
          <path
            d="M 18 46 Q 50 18 82 46"
            fill={color}
            opacity="0.18"
          />
          <path
            d="M 18 46 Q 50 18 82 46"
            fill="none"
            stroke={color}
            strokeWidth="2.2"
            strokeLinejoin="round"
          />
          {/* Canopy ribs */}
          <path d="M 30 41 Q 30 46 32 46" fill="none" stroke={color} strokeWidth="1.4" />
          <path d="M 50 30 L 50 46" stroke={color} strokeWidth="1.4" />
          <path d="M 70 41 Q 70 46 68 46" fill="none" stroke={color} strokeWidth="1.4" />
          <line x1="18" y1="46" x2="82" y2="46" stroke={color} strokeWidth="1.4" />
        </g>
      ) : (
        // Folded umbrella
        <g>
          <path d="M 44 22 L 56 22 L 60 70 L 40 70 Z" fill={color} opacity="0.18" stroke={color} strokeWidth="2" strokeLinejoin="round" />
        </g>
      )}
      {/* Handle */}
      <path
        d={open ? "M 50 46 L 50 78 Q 50 84 56 84" : "M 50 70 L 50 80 Q 50 86 56 86"}
        fill="none"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
      />
      {/* Tip (only when open) */}
      {open && <circle cx="50" cy="30" r="2" fill={color} />}
    </svg>
  );
}

/** 💧 Sponge being squeezed — "Air moisture" */
function SpongeMetaphor({ level, color }) {
  const drips = Math.max(0, Math.min(6, level));
  const wetness = level / 5;
  return (
    <svg viewBox="0 0 100 90" className="w-full h-full" aria-hidden>
      {/* Sponge body — slightly squashed when wetter */}
      <motion.g
        initial={{ scaleY: 1 }}
        animate={{ scaleY: [1, 0.94, 1] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        style={{ transformOrigin: "50px 35px" }}
      >
        <rect
          x="22"
          y="20"
          width="56"
          height="32"
          rx="6"
          fill={color}
          opacity={0.12 + wetness * 0.20}
        />
        <rect
          x="22"
          y="20"
          width="56"
          height="32"
          rx="6"
          fill="none"
          stroke={color}
          strokeWidth="2.2"
        />
        {/* Sponge holes */}
        {[
          [32, 30], [44, 28], [56, 32], [68, 27], [38, 40], [52, 42], [64, 39],
        ].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={1.6 + (i % 2)} fill={color} opacity="0.55" />
        ))}
        {/* Squeeze hands suggestion (left + right tabs) */}
        <path d="M 16 28 Q 12 36 16 44" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
        <path d="M 84 28 Q 88 36 84 44" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
      </motion.g>
      {/* Falling drips */}
      {Array.from({ length: drips }).map((_, i) => (
        <motion.path
          key={i}
          d={`M ${30 + i * 8} 56 Q ${30 + i * 8 - 1.5} 60 ${30 + i * 8} 64 Q ${30 + i * 8 + 1.5} 60 ${30 + i * 8} 56 Z`}
          fill={color}
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: [0, 1, 0], y: [0, 22, 28] }}
          transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.22, ease: "easeIn" }}
        />
      ))}
    </svg>
  );
}

/** ⛽ Fuel-gauge needle — "Storm fuel" */
function FuelGauge({ level, color }) {
  // 0 (empty) → 5 (full): -90° → 90°
  const angle = -90 + (Math.max(0, Math.min(5, level)) / 5) * 180;
  return (
    <svg viewBox="0 0 100 90" className="w-full h-full" aria-hidden>
      {/* Gauge dial outer */}
      <path d="M 18 70 A 32 32 0 0 1 82 70" fill={color} opacity="0.10" />
      <path d="M 18 70 A 32 32 0 0 1 82 70" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
      {/* Tick marks */}
      {[-90, -45, 0, 45, 90].map((a, i) => {
        const rad = (a * Math.PI) / 180;
        const r1 = 28;
        const r2 = 32;
        const x1 = 50 + r1 * Math.sin(rad);
        const y1 = 70 - r1 * Math.cos(rad);
        const x2 = 50 + r2 * Math.sin(rad);
        const y2 = 70 - r2 * Math.cos(rad);
        return (
          <line key={i} x1={x1.toFixed(1)} y1={y1.toFixed(1)} x2={x2.toFixed(1)} y2={y2.toFixed(1)} stroke={color} strokeWidth="1.5" opacity="0.8" />
        );
      })}
      {/* E and F labels */}
      <text x="18" y="80" fontSize="9" fontWeight="800" fill={color} fontFamily="Manrope, sans-serif">E</text>
      <text x="78" y="80" fontSize="9" fontWeight="800" fill={color} fontFamily="Manrope, sans-serif">F</text>
      {/* Needle */}
      <motion.g
        initial={{ rotate: -90 }}
        animate={{ rotate: angle }}
        transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformOrigin: "50px 70px" }}
      >
        <line x1="50" y1="70" x2="50" y2="38" stroke={color} strokeWidth="2.6" strokeLinecap="round" />
        <circle cx="50" cy="36" r="2.4" fill={color} />
      </motion.g>
      {/* Hub */}
      <circle cx="50" cy="70" r="4" fill={color} />
      <circle cx="50" cy="70" r="2" fill="#ffffff" opacity="0.6" />
    </svg>
  );
}

/** 🌀 Stacked wind ribbons — "Wind stirring" */
function WindRibbons({ level, color }) {
  // More turbulent ribbons as level rises
  const intensity = Math.max(0, Math.min(5, level));
  const ribbons = [
    { y: 30, len: 64, dur: 4 },
    { y: 46, len: 72, dur: 3.2 },
    { y: 62, len: 56, dur: 3.6 },
  ];
  const wave = (y, amp) =>
    `M 12 ${y} Q 30 ${y - amp} 50 ${y} T 88 ${y}`;

  return (
    <svg viewBox="0 0 100 90" className="w-full h-full" aria-hidden>
      {ribbons.map((r, i) => {
        const amp = 2 + intensity * (i === 1 ? 1.4 : 1);
        return (
          <motion.path
            key={i}
            d={wave(r.y, amp)}
            fill="none"
            stroke={color}
            strokeWidth={2.2 - i * 0.3}
            strokeLinecap="round"
            opacity={0.4 + i * 0.18}
            initial={{ pathLength: 0 }}
            animate={{
              pathLength: 1,
              x: [0, intensity > 0 ? 4 : 0, 0],
            }}
            transition={{
              pathLength: { duration: 1.2, delay: i * 0.18 },
              x: { duration: r.dur, repeat: Infinity, ease: "easeInOut" },
            }}
          />
        );
      })}
      {/* End-arrow heads on the right of each ribbon when stirring is real */}
      {intensity >= 3 &&
        ribbons.map((r, i) => (
          <motion.path
            key={`a-${i}`}
            d={`M 84 ${r.y - 3} L 90 ${r.y} L 84 ${r.y + 3}`}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.5 + i * 0.15}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0.55] }}
            transition={{ duration: 1.4, delay: 0.8 + i * 0.15 }}
          />
        ))}
      {/* Subtle swirl at the right when shear is strong */}
      {intensity >= 4 && (
        <motion.path
          d="M 76 40 Q 88 46 78 56 Q 70 60 72 50"
          fill="none"
          stroke={color}
          strokeWidth="1.6"
          strokeLinecap="round"
          opacity="0.55"
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
          style={{ transformOrigin: "78px 50px" }}
        />
      )}
    </svg>
  );
}

/** 🌡️ Thermometer with mood face in the bulb — "Outside feel" */
function ThermometerFace({ face, color, feelsLike }) {
  // mercury fills based on the face state
  const fillMap = { happy: 0.32, neutral: 0.5, warm: 0.7, hot: 0.86, danger: 0.96 };
  const fill = fillMap[face] || 0.5;
  // face features
  const renderFace = () => {
    const cx = 50;
    const cy = 70;
    // eyes
    const eyes = (
      <>
        <circle cx={cx - 4} cy={cy - 2} r="1.1" fill="#1c1917" />
        <circle cx={cx + 4} cy={cy - 2} r="1.1" fill="#1c1917" />
      </>
    );
    if (face === "happy") {
      return (
        <g>
          {eyes}
          <path d={`M ${cx - 5} ${cy + 2} Q ${cx} ${cy + 6} ${cx + 5} ${cy + 2}`} fill="none" stroke="#1c1917" strokeWidth="1.4" strokeLinecap="round" />
        </g>
      );
    }
    if (face === "neutral") {
      return (
        <g>
          {eyes}
          <line x1={cx - 4} y1={cy + 3} x2={cx + 4} y2={cy + 3} stroke="#1c1917" strokeWidth="1.4" strokeLinecap="round" />
        </g>
      );
    }
    if (face === "warm") {
      return (
        <g>
          {eyes}
          <path d={`M ${cx - 4} ${cy + 4} Q ${cx} ${cy + 1} ${cx + 4} ${cy + 4}`} fill="none" stroke="#1c1917" strokeWidth="1.4" strokeLinecap="round" />
        </g>
      );
    }
    if (face === "hot" || face === "danger") {
      return (
        <g>
          <line x1={cx - 5} y1={cy - 3} x2={cx - 3} y2={cy - 1} stroke="#1c1917" strokeWidth="1.4" strokeLinecap="round" />
          <line x1={cx - 3} y1={cy - 3} x2={cx - 5} y2={cy - 1} stroke="#1c1917" strokeWidth="1.4" strokeLinecap="round" />
          <line x1={cx + 3} y1={cy - 3} x2={cx + 5} y2={cy - 1} stroke="#1c1917" strokeWidth="1.4" strokeLinecap="round" />
          <line x1={cx + 5} y1={cy - 3} x2={cx + 3} y2={cy - 1} stroke="#1c1917" strokeWidth="1.4" strokeLinecap="round" />
          <path d={`M ${cx - 4} ${cy + 5} Q ${cx} ${cy + 1} ${cx + 4} ${cy + 5}`} fill="none" stroke="#1c1917" strokeWidth="1.4" strokeLinecap="round" />
          {/* Sweat drop */}
          <motion.path
            d={`M ${cx + 7} ${cy - 4} Q ${cx + 6} ${cy} ${cx + 7} ${cy + 2}`}
            fill={color}
            initial={{ opacity: 0, y: -2 }}
            animate={{ opacity: [0, 1, 0], y: [-2, 4, 6] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeIn" }}
          />
        </g>
      );
    }
    return eyes;
  };
  return (
    <svg viewBox="0 0 100 90" className="w-full h-full" aria-hidden>
      {/* Tube */}
      <rect x="44" y="14" width="12" height="48" rx="6" fill={color} opacity="0.10" />
      <rect x="44" y="14" width="12" height="48" rx="6" fill="none" stroke={color} strokeWidth="2" />
      {/* Mercury column inside tube */}
      <motion.rect
        x="46.4"
        y={14 + (1 - fill) * 48}
        width="7.2"
        height={fill * 48}
        rx="3.6"
        fill={color}
        initial={{ height: 0, y: 62 }}
        animate={{ height: fill * 48, y: 14 + (1 - fill) * 48 }}
        transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
      />
      {/* Tick marks on the side */}
      {[20, 30, 40, 50].map((y) => (
        <line key={y} x1="36" y1={y} x2="40" y2={y} stroke={color} strokeWidth="1.2" opacity="0.7" />
      ))}
      {/* Bulb with face */}
      <circle cx="50" cy="70" r="11" fill={color} opacity="0.18" />
      <circle cx="50" cy="70" r="11" fill="none" stroke={color} strokeWidth="2" />
      {renderFace()}
      {/* Feels-like badge */}
      {feelsLike != null && (
        <text x="80" y="22" fontSize="10" fontWeight="800" fill={color} fontFamily="Manrope, sans-serif" textAnchor="end">
          {Math.round(feelsLike)}°
        </text>
      )}
    </svg>
  );
}

/** ✈️ Plane on runway with weather glyph — "Airport report" */
function PlaneRunway({ mood, color }) {
  return (
    <svg viewBox="0 0 100 90" className="w-full h-full" aria-hidden>
      {/* Sky/cloud weather glyph */}
      {mood === "rain" && (
        <g>
          <path d="M 28 18 Q 22 22 26 28 Q 22 34 30 34 L 56 34 Q 64 34 62 28 Q 66 22 60 18 Q 56 12 48 16 Q 40 12 28 18 Z" fill={color} opacity="0.18" />
          <path d="M 28 18 Q 22 22 26 28 Q 22 34 30 34 L 56 34 Q 64 34 62 28 Q 66 22 60 18 Q 56 12 48 16 Q 40 12 28 18 Z" fill="none" stroke={color} strokeWidth="2" />
          {[34, 42, 50].map((x, i) => (
            <motion.line
              key={i}
              x1={x}
              y1={36}
              x2={x - 2}
              y2={44}
              stroke={color}
              strokeWidth="1.6"
              strokeLinecap="round"
              initial={{ opacity: 0, y: -2 }}
              animate={{ opacity: [0, 1, 0], y: [0, 6, 8] }}
              transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.18, ease: "easeIn" }}
            />
          ))}
        </g>
      )}
      {mood === "storm" && (
        <g>
          <path d="M 28 18 Q 22 22 26 28 Q 22 34 30 34 L 56 34 Q 64 34 62 28 Q 66 22 60 18 Q 56 12 48 16 Q 40 12 28 18 Z" fill={color} opacity="0.22" />
          <path d="M 28 18 Q 22 22 26 28 Q 22 34 30 34 L 56 34 Q 64 34 62 28 Q 66 22 60 18 Q 56 12 48 16 Q 40 12 28 18 Z" fill="none" stroke={color} strokeWidth="2" />
          <motion.path
            d="M 44 34 L 40 44 L 46 44 L 42 52"
            fill="none"
            stroke="#facc15"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ opacity: 0.6 }}
            animate={{ opacity: [0.5, 1, 0.6] }}
            transition={{ duration: 1.2, repeat: Infinity }}
          />
        </g>
      )}
      {mood === "calm" && (
        <g>
          {/* Sun */}
          <circle cx="44" cy="22" r="7" fill={color} opacity="0.18" />
          <circle cx="44" cy="22" r="7" fill="none" stroke={color} strokeWidth="2" />
          {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => {
            const r = (a * Math.PI) / 180;
            const x1 = 44 + 10 * Math.cos(r);
            const y1 = 22 + 10 * Math.sin(r);
            const x2 = 44 + 13 * Math.cos(r);
            const y2 = 22 + 13 * Math.sin(r);
            return <line key={a} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth="1.6" strokeLinecap="round" />;
          })}
        </g>
      )}
      {/* Runway */}
      <line x1="8" y1="74" x2="92" y2="74" stroke={color} strokeWidth="2" />
      <line x1="14" y1="78" x2="22" y2="78" stroke={color} strokeWidth="1.4" />
      <line x1="30" y1="78" x2="38" y2="78" stroke={color} strokeWidth="1.4" />
      <line x1="46" y1="78" x2="54" y2="78" stroke={color} strokeWidth="1.4" />
      <line x1="62" y1="78" x2="70" y2="78" stroke={color} strokeWidth="1.4" />
      <line x1="78" y1="78" x2="86" y2="78" stroke={color} strokeWidth="1.4" />
      {/* Plane */}
      <motion.g
        initial={{ x: -10, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: "easeOut" }}
      >
        {/* Body */}
        <path d="M 60 62 L 78 64 L 82 66 L 78 68 L 60 70 L 56 68 Q 54 66 56 64 Z" fill={color} opacity="0.85" />
        {/* Tail fin */}
        <path d="M 76 64 L 80 58 L 82 64 Z" fill={color} />
        {/* Wing */}
        <path d="M 64 66 L 70 56 L 72 66 Z" fill={color} opacity="0.7" />
        {/* Window */}
        <circle cx="74" cy="66" r="1" fill="#fdfaf2" />
      </motion.g>
    </svg>
  );
}

/* ============================================================
 * UI atoms
 * ============================================================ */

function ComicPanel({ illustration, title, word, sub, tone, theme, testId }) {
  const t = TONE[tone] || TONE.sky;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-[18px] relative overflow-hidden"
      style={{
        background: theme.surface,
        border: `1px solid ${t.border}`,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        boxShadow: "0 1px 0 rgba(255,255,255,0.55) inset, 0 8px 22px -16px rgba(20,12,4,0.16)",
      }}
      data-testid={testId}
    >
      {/* Soft tone wash */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(120% 80% at 0% 50%, ${t.soft} 0%, transparent 70%)`,
        }}
      />
      <div className="relative flex items-center gap-3 px-3 py-3">
        <div className="shrink-0 w-[88px] h-[80px]" style={{ color: t.ink }}>
          {illustration}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[9px] font-extrabold uppercase tracking-[0.2em]"
            style={{ color: t.ink }}
          >
            {title}
          </div>
          <div
            className="mt-1 text-[15px] font-extrabold leading-tight"
            style={{ color: theme.textPrimary, letterSpacing: "-0.02em" }}
          >
            {word}
          </div>
          <p className="mt-0.5 text-[11px] leading-snug" style={{ color: theme.textSecondary }}>
            {sub}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

/* ============================================================
 * Main
 * ============================================================ */

export default function FriendlyPulse({ confidence, theme }) {
  if (!confidence) return null;

  const sp = computeStormPotential(confidence);
  const air = airMoisture(confidence.sounding);
  const fuel = stormFuel(confidence);
  const wind = windStirring(confidence.sounding);
  const cb = comfortBand(confidence.heat, confidence);
  const ms = metarStatus(confidence.metar);

  const heroTone = TONE[sp.tone] || TONE.sky;

  // Short mood-aware word for the umbrella tile (the hero already shows
  // the engine's full headline — repeating it inside the umbrella card
  // looks redundant).
  const umbrellaWord =
    sp.mood === "calm"
      ? "No rain"
      : sp.level === "heavy"
        ? "Heavy rain"
        : sp.level === "moderate"
          ? "Moderate rain"
          : sp.level === "light"
            ? "Light rain"
            : sp.mood === "storm"
              ? "Storms brewing"
              : sp.level === "high"
                ? "Very likely"
                : "Possible";
  const umbrellaSub =
    sp.mood === "calm"
      ? "Skies stay friendly — no umbrella needed."
      : sp.mood === "storm"
        ? "Lightning + thunder before any rain falls."
        : sp.level === "heavy"
          ? "Carry rain gear, not just an umbrella."
          : sp.level === "moderate"
            ? "Keep an umbrella ready."
            : sp.level === "light"
              ? "A light umbrella will do."
              : "Keep an umbrella within reach.";

  return (
    <div className="space-y-2.5" data-testid="friendly-pulse">
      {/* HERO band — single-line verdict in the engine's mood color */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="rounded-[18px] px-4 py-3 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${heroTone.soft} 0%, transparent 80%)`,
          border: `1px solid ${heroTone.border}`,
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
        }}
        data-testid="friendly-storm-hero"
      >
        <div
          className="text-[9px] font-extrabold uppercase tracking-[0.28em]"
          style={{ color: heroTone.ink }}
        >
          The sky says
        </div>
        <h3
          className="mt-0.5 text-[18px] font-extrabold leading-tight"
          style={{ color: theme.textPrimary, letterSpacing: "-0.02em" }}
        >
          {sp.title}
        </h3>
        <p className="mt-1 text-[12px] leading-snug" style={{ color: theme.textSecondary }}>
          {sp.sub}
        </p>
        {/* Tone bar */}
        <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: `${heroTone.ink}1a` }}>
          <motion.div
            className="h-full rounded-full"
            style={{ background: heroTone.ink }}
            initial={{ width: 0 }}
            animate={{ width: `${sp.pct}%` }}
            transition={{ duration: 0.85, ease: "easeOut" }}
          />
        </div>
      </motion.div>

      {/* COMIC STACK — single column, horizontal cards */}
      <div className="space-y-2.5" data-testid="friendly-comic-grid">
        {/* Will it rain — umbrella */}
        <ComicPanel
          illustration={<UmbrellaRain level={sp.pct >= 75 ? 5 : sp.pct >= 55 ? 4 : sp.pct >= 30 ? 3 : sp.pct >= 15 ? 2 : 1} color={heroTone.ink} />}
          title="Will it rain"
          word={umbrellaWord}
          sub={umbrellaSub}
          tone={sp.tone}
          theme={theme}
          testId="friendly-umbrella"
        />
        {/* Air moisture — sponge */}
        <ComicPanel
          illustration={<SpongeMetaphor level={air.level} color={TONE[tonByLevel(air.level)].ink} />}
          title="Air moisture"
          word={air.word}
          sub={air.desc}
          tone={tonByLevel(air.level)}
          theme={theme}
          testId="friendly-air-moisture"
        />
        {/* Storm fuel — fuel gauge */}
        <ComicPanel
          illustration={<FuelGauge level={fuel.level} color={TONE[tonByLevel(fuel.level)].ink} />}
          title="Storm fuel"
          word={fuel.word}
          sub={fuel.desc}
          tone={tonByLevel(fuel.level)}
          theme={theme}
          testId="friendly-storm-fuel"
        />
        {/* Wind stirring — ribbons */}
        <ComicPanel
          illustration={<WindRibbons level={wind.level} color={TONE[tonByLevel(wind.level)].ink} />}
          title="Wind stirring"
          word={wind.word}
          sub={wind.desc}
          tone={tonByLevel(wind.level)}
          theme={theme}
          testId="friendly-wind-stirring"
        />
        {/* Outside feel — thermometer face */}
        {cb && (
          <ComicPanel
            illustration={
              <ThermometerFace
                face={cb.face}
                color={
                  TONE[
                    cb.face === "danger" || cb.face === "hot"
                      ? "rose"
                      : cb.face === "warm"
                        ? "amber"
                        : cb.face === "neutral"
                          ? "sky"
                          : "emerald"
                  ].ink
                }
                feelsLike={cb.feels}
              />
            }
            title="Outside feel"
            word={cb.word}
            sub={cb.desc}
            tone={
              cb.face === "danger" || cb.face === "hot"
                ? "rose"
                : cb.face === "warm"
                  ? "amber"
                  : cb.face === "neutral"
                    ? "sky"
                    : "emerald"
            }
            theme={theme}
            testId="friendly-comfort"
          />
        )}
        {/* Airport report — plane runway */}
        {ms && (
          <ComicPanel
            illustration={
              <PlaneRunway
                mood={ms.mood}
                color={
                  TONE[
                    ms.mood === "rain" ? "sky" : ms.mood === "storm" ? "amber" : "emerald"
                  ].ink
                }
              />
            }
            title="Nearby airport"
            word={ms.word}
            sub={ms.desc}
            tone={ms.mood === "rain" ? "sky" : ms.mood === "storm" ? "amber" : "emerald"}
            theme={theme}
            testId="friendly-metar"
          />
        )}
      </div>
    </div>
  );
}
